import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2, ShieldCheck, RefreshCw, Wrench, CheckCircle2, XCircle } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import client, { formatApiError } from "@/lib/api";

export default function AdminMaintenance() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [rebuildBusy, setRebuildBusy] = useState(false);

  const loadDiag = useCallback(async () => {
    setDiagBusy(true);
    try {
      const { data } = await client.get("/admin/content-diagnostics");
      setDiag(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setDiagBusy(false); }
  }, []);

  useEffect(() => { loadDiag(); }, [loadDiag]);

  const rebuild = async () => {
    setRebuildBusy(true);
    try {
      const { data } = await client.post("/admin/rebuild-content");
      setDiag(data.after);
      toast.success(`Content rebuilt — translation cache cleared (${data.i18n_cache_cleared}).`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setRebuildBusy(false); }
  };

  const runReset = async () => {
    if (confirm !== "RESET") return toast.error('Type RESET to confirm.');
    setBusy(true);
    try {
      const { data } = await client.post("/admin/reset-learning-progress", { confirm: "RESET" });
      setResult(data);
      toast.success(`Reset done — ${data.total_deleted} records deleted, ${data.profiles_reset} profiles reset.`);
      setOpen(false); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, ok }) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono font-semibold ${ok === false ? "text-red-600" : "text-slate-800"}`}>{value}</span>
    </div>
  );

  return (
    <div data-testid="admin-maintenance-page">
      <h1 className="font-heading text-2xl font-extrabold mb-1">Maintenance</h1>
      <p className="text-slate-500 mb-6">Content tools &amp; powerful, irreversible actions.</p>

      {/* Content diagnostics + rebuild */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-2xl mb-6" data-testid="content-diagnostics">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600"><Wrench size={22} /></span>
            <div>
              <h2 className="font-heading text-lg font-extrabold text-slate-900">Content status</h2>
              <p className="text-sm text-slate-500">What this live server actually has right now.</p>
            </div>
          </div>
          <button onClick={loadDiag} disabled={diagBusy} data-testid="diag-refresh-btn"
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-xl transition-colors">
            <RefreshCw size={15} className={diagBusy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {diag ? (
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid="diag-body">
            <div className="flex items-center gap-2 mb-2">
              {diag.in_sync
                ? <span className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-bold"><CheckCircle2 size={16} /> Content in sync with this build</span>
                : <span className="inline-flex items-center gap-1.5 text-amber-700 text-sm font-bold"><AlertTriangle size={16} /> Content NOT yet applied — click Rebuild</span>}
            </div>
            <Row label="Bundled dump version" value={(diag.dump_version || "—").slice(0, 20)} />
            <Row label="Seeded (DB) version" value={(diag.seeded_version || "—").slice(0, 20)} ok={diag.in_sync} />
            <div className="border-t border-slate-200 my-2" />
            {Object.entries(diag.sample_lessons || {}).map(([id, s]) => {
              const isEn = id.startsWith("en");
              const de = diag.sample_lessons[id.replace(/^en/, "de")];
              const mismatch = isEn && de && s.steps != null && de.steps != null && s.steps < de.steps * 0.7;
              return <Row key={id} label={`${id} steps`} value={s.steps ?? "—"} ok={mismatch ? false : undefined} />;
            })}
            <Row label="Translation cache entries" value={diag.i18n_cache_docs} />
          </div>
        ) : <p className="text-sm text-slate-400">Loading…</p>}

        <button onClick={rebuild} disabled={rebuildBusy} data-testid="rebuild-content-btn"
          className="mt-4 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold px-5 py-3 rounded-2xl transition-colors">
          {rebuildBusy ? <Loader2 className="animate-spin" size={17} /> : <RefreshCw size={17} />} Rebuild content &amp; clear cache
        </button>
        <p className="text-xs text-slate-400 mt-2">Re-applies the content shipped in this deployment and refreshes translations. Safe to run anytime.</p>
      </div>

      {/* Danger zone */}
      <div className="bg-white border-2 border-red-200 rounded-3xl p-6 max-w-2xl" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 shrink-0 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600"><AlertTriangle size={22} /></span>
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-extrabold text-slate-900">Reset all learning progress</h2>
            <p className="text-sm text-slate-600 mt-1">
              Deletes every user's lesson progress, collected words &amp; phrases, mistakes, activity,
              achievements and streaks, and sends everyone back to A1.
            </p>
            <div className="mt-3 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl p-3">
              <ShieldCheck size={18} className="shrink-0 mt-0.5" />
              <span>User <b>accounts</b>, <b>subscriptions</b> and <b>credits</b> are kept. Only learning data is wiped.</span>
            </div>
            <button onClick={() => setOpen(true)} data-testid="reset-progress-open-btn"
              className="mt-4 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-3 rounded-2xl transition-colors">
              <Trash2 size={17} /> Reset all progress…
            </button>
          </div>
        </div>
        {result && (
          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="reset-result">
            <p className="text-sm font-semibold text-slate-700">Last reset: {result.total_deleted} records deleted · {result.profiles_reset} profiles reset · {result.kept?.users} accounts kept</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) { setOpen(false); setConfirm(""); } }}>
        <DialogContent className="max-w-[460px] rounded-3xl border-0 p-0 overflow-hidden bg-white" data-testid="reset-confirm-modal">
          <div className="px-6 pt-7 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600"><AlertTriangle size={22} /></span>
              <div>
                <h2 className="font-heading text-lg font-extrabold text-slate-900">This cannot be undone</h2>
                <p className="text-sm text-slate-500">All learning progress will be permanently deleted.</p>
              </div>
            </div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Type <b>RESET</b> to confirm</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
              autoFocus placeholder="RESET"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold tracking-widest outline-none focus:border-red-500" />
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setOpen(false); setConfirm(""); }} disabled={busy} data-testid="reset-cancel-btn"
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-2xl transition-colors">Cancel</button>
              <button onClick={runReset} disabled={busy || confirm !== "RESET"} data-testid="reset-confirm-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-3 rounded-2xl transition-colors flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={17} />} Delete everything
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
